import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

function useEscape(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

/** Centred dialog — used for confirmations. */
export function Modal({ open, onClose, title, description, footer, children, width = "max-w-md" }) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="overlay" onClick={onClose} />
      <div className={cn("relative z-50 w-full neu-card p-6 animate-scale-in", width)}>
        <h3 className="text-base font-extrabold text-brand-blue">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        {children && <div className="mt-5">{children}</div>}
        {footer && <div className="flex justify-end gap-2 mt-6">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Right-side drawer for forms, matching the admin panel's user/course drawers. */
export function Drawer({ open, onClose, title, subtitle, footer, children, width = 440 }) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <div className="overlay" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full max-w-full bg-surface shadow-drawer flex flex-col z-50 animate-slide-in-right" style={{ width }}>
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-extrabold text-brand-blue">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-xl neu-pressable text-slate-500" aria-label="Close"><X size={15} /></button>
        </div>
        <div className="neu-divider mx-5" />
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer && (
          <>
            <div className="neu-divider mx-5" />
            <div className="flex justify-end gap-2 px-5 py-4">{footer}</div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
